/**
 * Shared date formatting utility.
 *
 * Single source of truth for the "DD Mmm YYYY" presentation format used on
 * the PDF cover (e.g. "05 Aug 2026") and now also embedded inside the engine's
 * pre-assembled `projectDuration.label` (D8 closure — Permanent Rule 12).
 *
 * Uses LOCAL accessors to round-trip parseP6Date's wall-clock interpretation
 * of .xer timestamps. P6 .xer date strings carry no timezone qualifier and
 * are parsed as local-time (parser/dataBuilder.js:47 uses `new Date(yyyy,
 * mm-1, dd, HH, MM)`). Reading them back with getUTC* would shift a 00:00
 * stamp into the previous day for any host east of UTC (e.g. Asia/Riyadh +3
 * silently rendered "2025-12-15 00:00" as "14 Dec 2025"). LOCAL accessors
 * preserve the wall-clock value the planner sees in P6.
 *
 * @module utils/dates
 */

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format a Date (or parseable date input) as "DD Mmm YYYY" with leading-zero day.
 * Returns "—" for null / undefined / invalid input.
 *
 * @param {Date | string | number | null | undefined} d
 * @returns {string}
 */
export function formatDateDDMonYYYY(d) {
  if (d === null || d === undefined || d === '') return '—';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = MONTH_ABBR[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

/**
 * Format a Date as local "YYYY-MM-DD". Uses LOCAL accessors for the same
 * wall-clock reason as formatDateDDMonYYYY — toISOString() would shift a P6
 * 00:00 local stamp into the previous day for hosts east of UTC. Returns
 * 'unknown' for null / undefined / invalid input.
 *
 * @param {Date | string | number | null | undefined} d
 * @returns {string}
 */
export function formatDateISO(d) {
  if (d === null || d === undefined || d === '') return 'unknown';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return 'unknown';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
